import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isDemoMode as libIsDemoMode } from '../lib/supabase';
import { fetchCachedUserProfile } from '../lib/userProfileCache';
import { Organization } from '../hooks/useOrganizations';
import { setUserContext, setOrganizationContext, setCompetitionContext, addBreadcrumb } from '../lib/sentry';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  currentOrganization: Organization | null;
  activeCompetitionId: string | null;
  loading: boolean;
  isDemoMode: boolean;
  passwordResetRequired: boolean;
  checkPasswordResetRequired: () => Promise<boolean>;
  setCurrentOrganization: (organization: Organization | null) => void;
  setActiveCompetitionId: (competitionId: string | null) => void;
  signUp: (email: string, password: string, firstName: string, lastName: string, organizationSlug?: string, phoneNumber?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: (redirectTo?: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  sendPasswordResetEmail: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
  resendEmailConfirmation: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to restore organization from localStorage with proper Date handling
const restoreOrganizationFromStorage = (): Organization | null => {
  try {
    const stored = localStorage.getItem('footy_current_organization');
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    // Convert date strings back to Date objects
    if (parsed.createdAt) parsed.createdAt = new Date(parsed.createdAt);
    if (parsed.updatedAt) parsed.updatedAt = new Date(parsed.updatedAt);
    return parsed as Organization;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);

  // Persist organization and competition ID to localStorage
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(restoreOrganizationFromStorage);

  const [activeCompetitionId, setActiveCompetitionIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem('footy_active_competition_id');
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);

  // Wrapper to persist organization to localStorage (memoized to prevent infinite loops)
  const setCurrentOrganization = useCallback((organization: Organization | null) => {
    setCurrentOrganizationState(organization);
    try {
      if (organization) {
        localStorage.setItem('footy_current_organization', JSON.stringify(organization));
      } else {
        localStorage.removeItem('footy_current_organization');
      }
    } catch (error) {
      console.warn('Failed to persist organization to localStorage:', error);
    }
  }, []);

  // Wrapper to persist competition ID to localStorage (memoized to prevent infinite loops)
  const setActiveCompetitionId = useCallback((competitionId: string | null) => {
    console.log('[AuthContext] setActiveCompetitionId called with:', competitionId);
    console.trace('[AuthContext] setActiveCompetitionId stack trace');
    setActiveCompetitionIdState(competitionId);
    try {
      if (competitionId) {
        localStorage.setItem('footy_active_competition_id', competitionId);
      } else {
        localStorage.removeItem('footy_active_competition_id');
      }
    } catch (error) {
      console.warn('Failed to persist competition ID to localStorage:', error);
    }
  }, []);

  useEffect(() => {
    if (libIsDemoMode) {
      // In demo mode, create a mock user
      const mockUser = {
        id: 'demo-user-123',
        email: 'demo@example.com',
        user_metadata: {
          first_name: 'Demo',
          last_name: 'User'
        }
      } as any as User;
      
      setUser(mockUser);
      setSession({ user: mockUser } as Session);
      setLoading(false);
      return;
    }

    // Get initial session
    let currentUserId: string | null = null;

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Session retrieval error:', error);
        setSession(null);
        setUser(null);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
        currentUserId = session?.user?.id ?? null;
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const newUserId = session?.user?.id ?? null;

      // Skip redundant state updates when the same user is re-confirmed
      // (e.g., tab visibility changes trigger duplicate SIGNED_IN events)
      if (event === 'SIGNED_IN' && newUserId === currentUserId && currentUserId !== null) {
        console.log('[AuthContext] Skipping redundant SIGNED_IN for same user');
        return;
      }

      console.log('[AuthContext] Auth state change:', {
        event,
        hasSession: !!session,
        userId: newUserId,
        email: session?.user?.email,
        provider: session?.user?.app_metadata?.provider
      });

      currentUserId = newUserId;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Update Sentry user context
      if (session?.user) {
        setUserContext(
          session.user.id,
          session.user.email,
          {
            provider: session.user.app_metadata?.provider,
            firstName: session.user.user_metadata?.first_name,
            lastName: session.user.user_metadata?.last_name,
          }
        );
        addBreadcrumb(`User ${event}`, 'auth', 'info', {
          userId: session.user.id,
          email: session.user.email,
        });
      } else {
        setUserContext(null);
        addBreadcrumb('User signed out', 'auth', 'info');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Update Sentry organization context when it changes
  useEffect(() => {
    if (currentOrganization) {
      setOrganizationContext(currentOrganization.id, currentOrganization.name);
      addBreadcrumb('Organization changed', 'navigation', 'info', {
        organizationId: currentOrganization.id,
        organizationName: currentOrganization.name,
      });
    } else {
      setOrganizationContext(null);
    }
  }, [currentOrganization]);

  // Update Sentry competition context when it changes
  useEffect(() => {
    if (activeCompetitionId) {
      setCompetitionContext(activeCompetitionId);
      addBreadcrumb('Competition changed', 'navigation', 'info', {
        competitionId: activeCompetitionId,
      });
    } else {
      setCompetitionContext(null);
    }
  }, [activeCompetitionId]);

  const signUp = async (email: string, password: string, firstName: string, lastName: string, organizationSlug?: string, phoneNumber?: string) => {
    if (libIsDemoMode) {
      return { error: null };
    }

    const metadata: Record<string, string> = {
      first_name: firstName,
      last_name: lastName,
    };

    // Include organization slug if provided so the database trigger can add user to that organization
    if (organizationSlug) {
      metadata.organization_slug = organizationSlug;
    }

    // Include phone number if provided
    if (phoneNumber) {
      metadata.phone_number = phoneNumber;
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    if (libIsDemoMode) {
      return { error: null };
    }
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    if (libIsDemoMode) {
      return { error: null };
    }
    
    try {
      const { error } = await supabase.auth.signOut();
      
      // If sign out returns an error (e.g., 403 from expired session), 
      // still clear local state so user can log in again
      if (error) {
        console.warn('Sign out returned error, clearing local session:', error.message);
        setSession(null);
        setUser(null);
        // Clear any stored session data
        try {
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('sb-ibztzxgdbluqzisqkiry-auth-token');
        } catch (e) {
          // Ignore localStorage errors
        }
        return { error: null }; // Return no error since we've handled it
      }
      
      return { error };
    } catch (err) {
      // If sign out fails with an exception, clear the local session anyway
      console.warn('Sign out failed, clearing local session:', err);
      
      // Force clear the local session
      setSession(null);
      setUser(null);
      
      // Clear any stored session data
      try {
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('sb-ibztzxgdbluqzisqkiry-auth-token');
      } catch (e) {
        // Ignore localStorage errors
      }
      
      return { error: null };
    }
  };

  const signInWithGoogle = async (redirectTo?: string) => {
    if (libIsDemoMode) {
      return { error: null };
    }

    const finalRedirectTo = redirectTo || `${window.location.origin}`;
    console.log('[AuthContext] Starting Google OAuth with redirect:', finalRedirectTo);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: finalRedirectTo
      }
    });

    console.log('[AuthContext] Google OAuth response:', {
      hasData: !!data,
      url: data?.url,
      provider: data?.provider,
      error: error?.message
    });

    return { error };
  };

  const sendPasswordResetEmail = async (email: string) => {
    if (libIsDemoMode) {
      return { error: null };
    }

    // Always redirect to the main domain where the reset password page is located
    // This ensures the redirect works regardless of which subdomain the user is on
    const resetPasswordUrl = 'https://footypicks.co.uk/reset-password';

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: resetPasswordUrl
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    if (libIsDemoMode) {
      return { error: null };
    }
    
    const { error } = await supabase.auth.updateUser({
      password: password
    });
    return { error };
  };

  const resendEmailConfirmation = async (email: string) => {
    if (libIsDemoMode) {
      return { error: null };
    }

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email
    });
    return { error };
  };

  const checkPasswordResetRequired = useCallback(async (): Promise<boolean> => {
    if (libIsDemoMode || !user) {
      return false;
    }

    try {
      const data = await fetchCachedUserProfile(user.id);

      const required = data?.password_reset_required || false;
      setPasswordResetRequired(required);
      return required;
    } catch (err) {
      console.error('Unexpected error checking password reset requirement:', err);
      return false;
    }
  }, [user]);

  useEffect(() => {
    if (user && !libIsDemoMode) {
      checkPasswordResetRequired();
    } else {
      setPasswordResetRequired(false);
    }
  }, [user, checkPasswordResetRequired]);

  const value = {
    user,
    session,
    currentOrganization,
    activeCompetitionId,
    loading,
    isDemoMode: libIsDemoMode,
    passwordResetRequired,
    checkPasswordResetRequired,
    setCurrentOrganization,
    setActiveCompetitionId,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    sendPasswordResetEmail,
    updatePassword,
    resendEmailConfirmation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};